import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import * as loginSelectors from '../../../../../../root-store/login-store/login.selector';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { LoginStoreEffects } from '@app/root-store/login-store/effects';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'manage-tag-search',
  templateUrl: './manage-tag-search.component.html',
  styleUrl: './manage-tag-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageTagSearchComponent implements OnInit, OnDestroy {
  @ViewChild('inputField', { read: ElementRef }) inputField: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @Input()
  set data(value: any[]) {
    this._data = value;
    this.updateProcessedData();
  }
  get data(): any[] {
    return this._data;
  }
  @Input() placeHolder = '';
  @Input() origin = '';
  @Input() disabled = false;
  @Input() errorMessage = '';
  @Input() attachmentTags = [];
  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() newItemCreationdEventEmitter: EventEmitter<any> = new EventEmitter<any>();

  currentUser;
  searchValue = '';
  loadOnce = false;
  customTagName = '';
  private _data = [];
  processedData = [];
  vaultTagForm: UntypedFormGroup;
  isInputFocused: boolean = false;
  isTagInputShown: boolean = false;
  subscription = new Subscription();
  connectionUserFormControl = new UntypedFormControl();

  constructor(private cd: ChangeDetectorRef, private loginService: LoginStoreEffects, private formBuilder: UntypedFormBuilder, private store: Store) {
    this.subscription.add(
      this.loginService.loginObject.subscribe((state) => {
        if (state?.data) {
          if (state?.data?.id && !this.loadOnce) {
            this.loadOnce = true;
          }
        }
      })
    );
  }

  ngOnInit(): void {
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe((result) => {
        if (result) {
          this.currentUser = result;
        }
      })
    );

    if (this.disabled) {
      this.connectionUserFormControl.disable();
    }

    this.updateProcessedData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachmentTags']) {
      this.updateProcessedData();
    }
    if (changes['errorMessage']) {
      this.errorMessage = changes['errorMessage'].currentValue;
    }
    if (changes['disabled']) {
      if (this.disabled) {
        this.connectionUserFormControl.disable();
        if (this.autocomplete && this.autocomplete.panelOpen) {
          this.autocomplete.closePanel();
        }
      } else {
        this.connectionUserFormControl.enable();
      }
      this.cd.detectChanges();
    }
  }

  private updateProcessedData(): void {
    const selectedSet = new Set(this.attachmentTags || []);
    this.processedData = this._data.map((entity) => ({
      ...entity,
      isSelected: selectedSet.has(entity.name),
    }));
  }

  handleKeyDown(event: KeyboardEvent) {
    const keysToPrevent = [' ', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
    if (event.target instanceof HTMLInputElement) {
      if (event.target.selectionStart === 0 && (event.key === ' ' || event.key === 'Tab')) {
        event.preventDefault();
        return;
      }
    }
    if (keysToPrevent.includes(event.key)) {
      event.stopPropagation();
    }
  }

  onCustomTagNameInputChange(value: string) {
    this.errorMessage = '';
    this.customTagName = value;
    this.cd.detectChanges();
  }

  onItemSearch(value) {
    this.connectionUserFormControl.setValue(value);
    this.cd.detectChanges();
  }

  createVaultUserTag(event: any, item: any) {
    const isChecked = event.checked;
    this.selectionChange.emit({
      tag: item,
      action: isChecked ? 'add' : 'remove',
      isChecked: isChecked,
    });
  }

  addUserTag() {
    if (!this.customTagName || !this.customTagName.trim()) {
      return;
    }
    this.newItemCreationdEventEmitter.emit(this.customTagName);
  }

  onInputFocus() {
    if (!this.disabled) {
      this.isInputFocused = true;
    } else {
      if (this.autocomplete && this.autocomplete.panelOpen) {
        this.autocomplete.closePanel();
      }
    }
  }

  onInputBlur() {
    this.isInputFocused = false;
  }

  onClearInput() {
    this.connectionUserFormControl.setValue('');
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
